/**
 * useAgentSessionList Hook - Clean React Implementation
 *
 * Provides session browsing, filtering, and list management functionality.
 * Updated to work with the new clean agent context implementation.
 *
 * 🚀 FIXES:
 * - Uses new context interface
 * - Stable references from context
 * - Clean session filtering and sorting
 */

import { useContext, useState, useCallback, useEffect } from 'react';
import AgentSessionContext from '../context/AgentSessionContext';

/**
 * Hook for session list management and browsing
 * @param {Object} [options={}] - Configuration options
 * @param {boolean} [options.autoRefresh=false] - Auto-refresh session list
 * @param {number} [options.refreshInterval=30000] - Refresh interval in ms
 * @returns {Object} Session list interface
 * @throws {Error} If used outside of AgentSessionProvider
 */
const useAgentSessionList = (options = {}) => {
  const context = useContext(AgentSessionContext);

  if (!context) {
    throw new Error(
      'useAgentSessionList must be used within an AgentSessionProvider. ' +
        'Make sure your component is wrapped with <AgentSessionProvider>.'
    );
  }

  const { autoRefresh = false, refreshInterval = 30000 } = options;

  // Destructure stable callbacks to avoid full `context` in useCallback deps
  // (context object recreates on every messageUpdateTrigger change)
  const {
    createSession: ctxCreateSession,
    deleteSession: ctxDeleteSession,
    updateSession: ctxUpdateSession,
    searchSessions: ctxSearchSessions,
    getRecentSessions: ctxGetRecentSessions,
    refreshSessions: ctxRefreshSessions
  } = context;

  // Local state for filtered sessions and search
  const [filteredSessions, setFilteredSessions] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState(null);

  // TD-2013: Use centralized session list from provider (fetched once, shared by all hook instances)
  // Local overrides for optimistic updates (delete/rename) - falls back to provider state
  const [localSessionOverride, setLocalSessionOverride] = useState(null);
  const allSessions = localSessionOverride ?? context.allSessions;
  const isLoadingSessions = context.isLoadingSessions;

  // Sync local override back to null when provider state updates (e.g., after refresh)
  useEffect(() => {
    setLocalSessionOverride(null);
  }, [context.allSessions]);

  // Auto-refresh functionality
  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(() => {
      // Trigger re-evaluation of filtered sessions
      // Note: In a real implementation, you might want to fetch from server here
      if (import.meta.env.DEV) {
        console.log('🔄 Auto-refreshing session list');
      }
    }, refreshInterval);

    return () => clearInterval(interval);
  }, [autoRefresh, refreshInterval]);

  // ✅ Session filtering and querying using new context methods
  const getRecentSessions = useCallback(
    async (limit = 10) => {
      return await ctxGetRecentSessions(limit);
    },
    [ctxGetRecentSessions]
  );

  const getSessionsByProject = useCallback(
    (projectId) => {
      return allSessions.filter((session) => session.projectId === projectId);
    },
    [allSessions]
  );

  const getArchivedSessions = useCallback(() => {
    return allSessions.filter((session) => session.isArchived);
  }, [allSessions]);

  const getFavoriteSessions = useCallback(() => {
    return allSessions.filter((session) => session.isFavorite);
  }, [allSessions]);

  /**
   * Search sessions using backend full-text search
   * NOTE: This backend search is no longer used by the UI
   * Previous popup modal used this for content search
   * Current /conversations page uses client-side title filtering instead
   *
   * Backend search searches:
   * - Session titles (session_name)
   * - Message content (all user and AI messages)
   * - Returns similarity scores and matched text excerpts
   */
  const searchSessions = useCallback(
    async (query, filters = {}) => {
      setIsSearching(true);
      setSearchError(null);

      try {
        // Use backend full-text search if query provided
        if (query && query.trim()) {
          if (import.meta.env.DEV) {
            console.log('🔍 HOOK: searchSessions called with query:', query);
            console.log('🔍 HOOK: allSessions available:', allSessions?.length || 0);
          }

          const searchResults = await ctxSearchSessions(query);
          if (import.meta.env.DEV) {
            console.log('🔍 HOOK: Backend search returned:', searchResults?.length || 0, 'results');
            if (searchResults && searchResults.length > 0) {
              console.log('🔍 HOOK: First backend result:', searchResults[0]);
            }
          }

          // Transform backend search results to session format
          // 🔥 FIX: Use backend data directly - don't require sessions to be in local cache
          // Backend search is the source of truth for which sessions match the query
          const sessionMap = new Map();
          for (const result of searchResults) {
            if (!sessionMap.has(result.sessionId)) {
              // Try to find full session from allSessions for additional metadata
              const fullSession = allSessions.find((s) => s.id === result.sessionId);
              if (import.meta.env.DEV) {
                console.log(
                  '🔍 HOOK: Looking for session',
                  result.sessionId,
                  'found:',
                  !!fullSession
                );
              }

              if (fullSession) {
                // Use full session data with search metadata
                sessionMap.set(result.sessionId, {
                  ...fullSession,
                  searchScore: result.similarityScore,
                  matchText: result.text
                });
              } else {
                // 🔥 FIX: Create minimal session object from backend search result
                // This ensures search results always display, even if not in local cache
                if (import.meta.env.DEV) {
                  console.log(
                    '🔍 HOOK: ✅ Creating session from backend search result:',
                    result.sessionId
                  );
                }
                sessionMap.set(result.sessionId, {
                  id: result.sessionId,
                  title: result.sessionName || result.text.substring(0, 100), // Use session_name as title, fallback to matched text
                  searchScore: result.similarityScore,
                  matchText: result.text,
                  createdAt: new Date(), // Placeholder - backend doesn't return this
                  lastActivity: new Date(), // Placeholder
                  isFavorite: result.isFavorite || false,
                  isArchived: result.isArchived || false
                });
              }
            }
          }

          let results = Array.from(sessionMap.values());
          if (import.meta.env.DEV) {
            console.log('🔍 HOOK: Final mapped results:', results.length);
          }

          // Apply additional filters
          if (filters.isArchived !== undefined) {
            results = results.filter((s) => s.isArchived === filters.isArchived);
          }

          if (filters.isFavorited !== undefined) {
            results = results.filter((s) => s.isFavorite === filters.isFavorited);
          }

          if (filters.dateRange) {
            const { start, end } = filters.dateRange;
            results = results.filter((s) => {
              const sessionDate = s.createdAt;
              return sessionDate >= start && sessionDate <= end;
            });
          }

          // Sort by search score (already sorted by backend, but ensure)
          results.sort((a, b) => (b.searchScore || 0) - (a.searchScore || 0));

          setFilteredSessions(results);
          setIsSearching(false);
          return results;
        }

        // No query - just filter allSessions
        let results = allSessions;

        // Apply filters
        if (filters.projectId) {
          results = results.filter((s) => s.projectId === filters.projectId);
        }

        if (filters.isArchived !== undefined) {
          results = results.filter((s) => s.isArchived === filters.isArchived);
        }

        if (filters.isFavorited !== undefined) {
          results = results.filter((s) => s.isFavorite === filters.isFavorited);
        }

        if (filters.dateRange) {
          const { start, end } = filters.dateRange;
          results = results.filter((s) => {
            const sessionDate = s.createdAt;
            return sessionDate >= start && sessionDate <= end;
          });
        }

        // Sort by last activity (most recent first)
        results.sort((a, b) => b.lastActivity - a.lastActivity);

        setFilteredSessions(results);
        setIsSearching(false);

        return results;
      } catch (error) {
        console.error('❌ Failed to search sessions:', error);
        setSearchError(error.message);
        setIsSearching(false);
        throw error;
      }
    },
    [allSessions, ctxSearchSessions]
  );

  // TD-2013: Delegate refresh to provider (single centralized fetch)
  const refreshSessions = useCallback(async () => {
    setLocalSessionOverride(null);
    return ctxRefreshSessions();
  }, [ctxRefreshSessions]);

  // ✅ Session management actions using new context
  const createSession = useCallback(
    async (initialMessage) => {
      return ctxCreateSession(initialMessage);
    },
    [ctxCreateSession]
  );

  const deleteSession = useCallback(
    async (sessionId) => {
      try {
        // Optimistic delete: Override local view immediately
        setLocalSessionOverride(allSessions.filter((s) => s.id !== sessionId));

        // Call backend to delete
        await ctxDeleteSession(sessionId);
      } catch (error) {
        console.error('❌ Failed to delete session:', error);
        setSearchError(error.message);

        // Rollback: Clear override so provider state takes effect
        setLocalSessionOverride(null);
        throw error;
      }
    },
    [allSessions, ctxDeleteSession]
  );

  const updateSession = useCallback(
    async (sessionId, updates) => {
      try {
        // Optimistic update: Override local view immediately
        setLocalSessionOverride(
          allSessions.map((s) =>
            s.id === sessionId
              ? {
                  ...s,
                  title: updates.name !== undefined ? updates.name : s.title,
                  isFavorite:
                    updates.is_favorite !== undefined ? updates.is_favorite : s.isFavorite,
                  isArchived: updates.is_archived !== undefined ? updates.is_archived : s.isArchived
                }
              : s
          )
        );

        // Call backend to update
        await ctxUpdateSession(sessionId, updates);
      } catch (error) {
        console.error('❌ Failed to update session:', error);
        setSearchError(error.message);

        // Rollback: Clear override so provider state takes effect
        setLocalSessionOverride(null);
        throw error;
      }
    },
    [allSessions, ctxUpdateSession]
  );

  return {
    // ============ SESSION DATA ============
    allSessions,
    filteredSessions,
    sessionCount: allSessions.length,

    // ============ SESSION QUERIES ============
    getRecentSessions,
    getSessionsByProject,
    getArchivedSessions,
    getFavoriteSessions,

    // ============ SEARCH & FILTERING ============
    searchSessions,
    isSearching,
    searchError,

    // ============ SESSION MANAGEMENT ============
    createSession,
    deleteSession: deleteSession,
    updateSession: updateSession,
    refreshSessions,

    // ============ STATE ============
    isLoading: context.isLoading || isLoadingSessions,
    error: context.error || searchError,
    clearError: context.clearError
  };
};

export default useAgentSessionList;
